import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Star, Heart } from "lucide-react";
import { useReserve } from "@/contexts/ReserveContext";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/menu", label: "Menu" },
  { to: "/drops", label: "Weekly Drops" },
  { to: "/vault", label: "The Vault" },
  { to: "/pumabilt", label: "The Pumabilt" },
  { to: "/membership", label: "Stewardship" },
  { to: "/events", label: "Events" },
  { to: "/house-alerts", label: "House Alerts" },
  { to: "/chime-in", label: "Chime In" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "backdrop-blur-xl bg-obsidian/70 border-b border-bone/10" : "bg-transparent"
      }`}
    >
      <nav className="mx-auto max-w-[1500px] px-6 md:px-10 h-16 md:h-20 flex items-center gap-6 xl:gap-8">
        <Link to="/" data-testid="brand-mark" className="group flex items-center gap-3 shrink-0">
          <span className="block h-2 w-2 rotate-45 bg-brass" />
          <span className="font-display text-bone text-lg md:text-xl tracking-wide whitespace-nowrap">
            The Uncanny House
          </span>
        </Link>

        <ul className="hidden lg:flex items-center gap-6 xl:gap-8">
          {LINKS.map((l) => (
            <li key={l.to}>
              <NavLink
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `text-[12px] uppercase tracking-[0.28em] whitespace-nowrap transition-colors duration-300 ${
                    isActive ? "text-brass" : "text-bone/70 hover:text-bone"
                  }`
                }
                end={l.to === "/"}
              >
                {l.label}
              </NavLink>
            </li>
          ))}
        </ul>

        <Link
          to="/membership"
          data-testid="nav-cta-house-list"
          className="hidden lg:inline-flex items-center px-5 py-2 text-[11px] uppercase tracking-[0.3em] whitespace-nowrap text-obsidian bg-lavender hover:bg-bone transition-colors duration-300"
        >
          Join The House List
        </Link>

        <div className="ml-auto flex items-center gap-4 shrink-0">
          <ReserveBadge />
          <ObsessionBadge />

          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden text-bone p-2"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </nav>

      {open && (
        <div
          data-testid="mobile-menu-panel"
          className="lg:hidden bg-obsidian/95 backdrop-blur-xl border-t border-bone/10"
        >
          <ul className="px-6 py-6 flex flex-col gap-5">
            {LINKS.map((l) => (
              <li key={l.to}>
                <NavLink
                  to={l.to}
                  data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `block text-base uppercase tracking-[0.28em] ${
                      isActive ? "text-brass" : "text-bone/80"
                    }`
                  }
                  end={l.to === "/"}
                >
                  {l.label}
                </NavLink>
              </li>
            ))}
            <li className="pt-4 border-t border-bone/10 space-y-3">
              <Link
                to="/membership"
                data-testid="mobile-nav-cta-house-list"
                className="block w-fit px-5 py-3 text-[11px] uppercase tracking-[0.3em] text-obsidian bg-lavender"
              >
                Join The House List
              </Link>
              <MobileReserveLinks />
            </li>
          </ul>
        </div>
      )}
    </header>
  );
}

/* ---------- Reserve & Obsession indicators ---------- */
function ReserveBadge() {
  const { reserveCount } = useReserve();
  return (
    <Link
      to="/reserve"
      data-testid="nav-reserve"
      className="hidden lg:inline-flex items-center gap-2 px-3 py-2 text-[11px] uppercase tracking-[0.28em] text-bone/80 hover:text-brass transition-colors"
      title="The Reserve"
    >
      <Star size={14} className="text-brass" />
      The Reserve
      <span
        data-testid="nav-reserve-count"
        className="ml-1 min-w-[22px] h-[22px] inline-flex items-center justify-center px-1.5 bg-brass text-obsidian text-[10px] font-medium tabular-nums rounded-full"
      >
        {reserveCount}
      </span>
    </Link>
  );
}

function ObsessionBadge() {
  const { obsessionCount } = useReserve();
  return (
    <Link
      to="/obsession-list"
      data-testid="nav-obsession"
      className="hidden lg:inline-flex items-center gap-2 px-3 py-2 text-[11px] uppercase tracking-[0.28em] text-bone/80 hover:text-lavender transition-colors"
      title="Obsession List"
    >
      <Heart size={14} className="text-lavender" />
      Obsession List
      <span
        data-testid="nav-obsession-count"
        className="ml-1 min-w-[22px] h-[22px] inline-flex items-center justify-center px-1.5 bg-lavender text-obsidian text-[10px] font-medium tabular-nums rounded-full"
      >
        {obsessionCount}
      </span>
    </Link>
  );
}

function MobileReserveLinks() {
  const { reserveCount, obsessionCount } = useReserve();
  return (
    <div className="flex flex-wrap gap-3 pt-3">
      <Link
        to="/reserve"
        data-testid="mobile-nav-reserve"
        className="inline-flex items-center gap-2 px-4 py-2 border border-brass/50 text-brass text-[11px] uppercase tracking-[0.28em]"
      >
        <Star size={13} /> The Reserve · {reserveCount}
      </Link>
      <Link
        to="/obsession-list"
        data-testid="mobile-nav-obsession"
        className="inline-flex items-center gap-2 px-4 py-2 border border-lavender/50 text-lavender text-[11px] uppercase tracking-[0.28em]"
      >
        <Heart size={13} /> Obsession List · {obsessionCount}
      </Link>
    </div>
  );
}
